import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Plus,
  MoreHorizontal,
  ChevronRight,
  Search,
  SlidersHorizontal,
  Settings,
  Users,
  Trash2,
  Lock,
  Film as FilmIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { cn } from '../lib/utils';

/* ----------------------------- helpers ----------------------------- */

// Status taxonomy → tailwind classes (badge pill).
const FILM_STATUS = {
  IN_REVIEW:       { label: 'In Review',       className: 'bg-primary/15 text-primary' },
  PRE_PRODUCTION:  { label: 'Pre-Production',  className: 'bg-warning/15 text-warning' },
  ON_HOLD:         { label: 'On Hold',         className: 'bg-muted text-muted-foreground' },
  IN_PRODUCTION:   { label: 'In Production',   className: 'bg-success/15 text-success' },
  COMPLETE:        { label: 'Complete',        className: 'bg-secondary text-secondary-foreground' },
};

// Status derivation per brief. NOTE: the cross-workspace `GET /api/films`
// payload omits scene/shot counts. When those are missing we honestly fall
// through to `IN_REVIEW` rather than fabricating a status.
// TODO(phase-X): replace with a real persisted `film.status` once the
// backend exposes one.
const deriveFilmStatus = (film) => {
  const sceneCount   = Number.isFinite(film.scene_count)        ? film.scene_count        : null;
  const shotCount    = Number.isFinite(film.shot_count)         ? film.shot_count         : null;
  const coveredShots = Number.isFinite(film.covered_shot_count) ? film.covered_shot_count : null;

  if (shotCount != null && shotCount > 0 && coveredShots === shotCount) return 'COMPLETE';
  if (shotCount != null && shotCount > 0 && coveredShots != null && coveredShots > 0) return 'IN_PRODUCTION';
  if (sceneCount != null && sceneCount > 0) return 'PRE_PRODUCTION';
  return 'IN_REVIEW';
};

// Stage label + progress percentage. Mirrors the status taxonomy.
const deriveStageProgress = (film, status) => {
  const sceneCount   = Number.isFinite(film.scene_count)        ? film.scene_count        : 0;
  const shotCount    = Number.isFinite(film.shot_count)         ? film.shot_count         : 0;
  const coveredShots = Number.isFinite(film.covered_shot_count) ? film.covered_shot_count : 0;

  switch (status) {
    case 'COMPLETE':
      return { label: 'Delivered', percent: 100 };
    case 'IN_REVIEW': {
      const pct = shotCount > 0 ? Math.round((coveredShots / shotCount) * 100) : 0;
      return { label: 'Post-Production', percent: pct };
    }
    case 'IN_PRODUCTION': {
      const pct = shotCount > 0 ? Math.round((coveredShots / shotCount) * 100) : 0;
      return { label: 'Principal Photography', percent: pct };
    }
    case 'PRE_PRODUCTION': {
      // proxy: scenes count vs an arbitrary 50-scene target
      const pct = Math.min(100, Math.round((sceneCount / 50) * 100));
      return { label: 'Script Finalization', percent: pct };
    }
    case 'ON_HOLD': {
      const pct = Math.min(100, Math.round((sceneCount / 10) * 100));
      return { label: 'Conceptualization', percent: pct };
    }
    default:
      return { label: 'Pre-Production', percent: 0 };
  }
};

const progressBarColorClass = (percent, status) => {
  if (status === 'COMPLETE') return 'bg-success';
  if (percent <= 0)          return 'bg-muted-foreground/30';
  if (percent < 50)          return 'bg-warning';
  return 'bg-primary';
};

// Activity heuristic. TODO(phase-X): replace with a real
// "scenes/shots updated in last 30d" signal — backend doesn't expose that yet.
const deriveWorkspaceActivity = (workspace) => {
  const films = Number(workspace?.active_film_count ?? workspace?.film_count ?? 0);
  if (films >= 10) return { label: 'High',   className: 'text-primary' };
  if (films >= 3)  return { label: 'Medium', className: 'text-foreground' };
  if (films >= 1)  return { label: 'Low',    className: 'text-muted-foreground' };
  return            { label: 'None',  className: 'text-muted-foreground/60' };
};

const formatRelative = (iso) => {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
};

const titleInitial = (title = '') => {
  const t = String(title).trim();
  return t ? t[0].toUpperCase() : '?';
};

/* --------------------------- subcomponents --------------------------- */

/**
 * PlanBadge — renders a compact uppercase chip for the workspace plan tier.
 *
 *   FREE  → neutral outlined (so it visually recedes vs PRO)
 *   PRO   → solid amber/gold-ish accent (uses --warning token to read as paid)
 *
 * We collapse plan_pro / plan_studio / plan_enterprise into a single "PRO"
 * label for this pass — the brief explicitly asked for this rollup.
 */
const PlanBadge = ({ isFree, testId }) => {
  if (isFree) {
    return (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-border bg-card text-muted-foreground text-[10px] font-bold tracking-[0.22em] uppercase"
        data-testid={testId}
      >
        Free
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-warning text-warning-foreground text-[10px] font-bold tracking-[0.22em] uppercase shadow-sm"
      data-testid={testId}
    >
      Pro
    </span>
  );
};

/**
 * WorkspaceRow — compact list row that mirrors the FilmRow layout below.
 * Replaces the previous huge `WorkspaceCard` (320-360px tall grid tile) so
 * the workspaces section reads like the "Your Films" list: header + searchable
 * rows. The full action set still lives behind the kebab menu.
 */
const WorkspaceRow = ({ workspace, isOwner, onOpen, onDelete, onSettings, onMembers }) => {
  const activity = deriveWorkspaceActivity(workspace);
  const isActive = (workspace.film_count ?? 0) > 0;
  const statusLabel = isActive ? 'Active' : 'Idle';
  const statusClass = isActive
    ? 'bg-primary/15 text-primary'
    : 'bg-muted text-muted-foreground';

  // Collapse plan_pro / plan_studio / plan_enterprise → "PRO". Only plan_free → "FREE".
  // Missing/unknown → FREE (never over-promise).
  const isFreePlan = (workspace.plan_id || 'plan_free') === 'plan_free';

  const filmCount = workspace.active_film_count ?? workspace.film_count ?? 0;
  const memberCount = workspace.member_count ?? 1;

  return (
    <div
      className="group flex items-center gap-4 p-4 md:p-5 hover:bg-muted/40 transition-colors"
      data-testid={`dc-workspace-card-${workspace.id}`}
    >
      {/* Thumbnail: logo or initial tile (mirrors FilmThumb sizing for visual parity) */}
      <WorkspaceThumb workspace={workspace} />

      {/* Middle: name + role/plan + inline stats */}
      <button
        type="button"
        onClick={() => onOpen(workspace)}
        className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-left"
        data-testid={`dc-workspace-open-${workspace.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <h3 className="font-display font-semibold text-lg md:text-xl tracking-tight truncate">
              {workspace.name}
            </h3>
            <span
              className={cn(
                'shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase',
                statusClass
              )}
            >
              {statusLabel}
            </span>
            <PlanBadge isFree={isFreePlan} testId={`dc-workspace-plan-${workspace.id}`} />
          </div>
          <p className="mt-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-muted-foreground truncate">
            {isOwner ? 'Owner' : 'Member'}
            {workspace.description ? ` · ${workspace.description}` : ''}
          </p>
        </div>

        {/* Inline stats: films · members · activity (mirrors films-row stage column width) */}
        <div className="md:w-72 lg:w-80 md:shrink-0 grid grid-cols-3 gap-3 md:gap-4">
          <InlineStat label="Films" value={filmCount} />
          <InlineStat label="Members" value={memberCount} />
          <InlineStat label="Activity" value={activity.label} valueClassName={activity.className} />
        </div>
      </button>

      {/* Trailing: action menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
            aria-label="Workspace actions"
            data-testid={`dc-workspace-menu-${workspace.id}`}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs">Actions</DropdownMenuLabel>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onOpen(workspace); }}>
            <ChevronRight className="size-4 mr-2" /> Open
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onMembers(workspace); }}>
            <Users className="size-4 mr-2" /> Members
          </DropdownMenuItem>
          {isOwner && (
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onSettings(workspace); }}>
              <Settings className="size-4 mr-2" /> Settings
            </DropdownMenuItem>
          )}
          {isOwner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(e) => { e.preventDefault(); onDelete(workspace); }}
                data-testid={`dc-workspace-delete-${workspace.id}`}
              >
                <Trash2 className="size-4 mr-2" /> Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const WorkspaceThumb = ({ workspace }) => {
  const initial = titleInitial(workspace.name);
  if (workspace.logo_url) {
    return (
      <img
        src={workspace.logo_url}
        alt=""
        className="size-12 md:size-14 rounded-xl object-cover shrink-0 ring-1 ring-border"
      />
    );
  }
  return (
    <div
      aria-hidden
      className="size-12 md:size-14 rounded-xl shrink-0 flex items-center justify-center bg-gradient-to-br from-primary/30 to-warning/30 ring-1 ring-border"
    >
      <span className="font-display font-bold text-lg md:text-xl text-foreground/80">{initial}</span>
    </div>
  );
};

const InlineStat = ({ label, value, valueClassName }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
      {label}
    </span>
    <span className={cn('font-display text-base md:text-lg font-semibold leading-tight truncate', valueClassName)}>
      {value}
    </span>
  </div>
);

const WorkspaceRowSkeleton = () => (
  <div className="flex items-center gap-4 p-4 md:p-5">
    <Skeleton className="size-12 md:size-14 rounded-xl shrink-0" />
    <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="md:w-72 lg:w-80 md:shrink-0 grid grid-cols-3 gap-3 md:gap-4">
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>
    </div>
    <Skeleton className="size-8 rounded-lg shrink-0" />
  </div>
);

const WorkspacesEmptyState = ({ onCreate, cleared }) => (
  <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
    <div className="mx-auto size-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
      <Plus className="size-7 text-primary" />
    </div>
    <h3 className="font-display font-semibold text-xl mb-1">
      {cleared ? 'No workspaces match' : 'No workspaces yet'}
    </h3>
    <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-5">
      {cleared
        ? 'Try a different search term or clear the filter to see all workspaces.'
        : 'Create a workspace to start organising your films, scenes and crew.'}
    </p>
    {onCreate && (
      <Button onClick={onCreate} size="sm" className="rounded-xl" data-testid="dc-workspaces-empty-cta">
        <Plus className="size-4 mr-1.5" />
        {cleared ? 'Clear filters' : 'Create Workspace'}
      </Button>
    )}
  </div>
);

const WORKSPACE_FILTER_OPTIONS = [
  { value: 'all',    label: 'All workspaces' },
  { value: 'owned',  label: 'Owned by you' },
  { value: 'member', label: 'Shared with you' },
];

const WorkspacesHeader = ({ search, onSearchChange, filter, onFilterChange, count, onCreate }) => {
  const activeFilterLabel = WORKSPACE_FILTER_OPTIONS.find((o) => o.value === filter)?.label || 'All';
  return (
    <div className="mb-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            Your Workspaces
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {count} {count === 1 ? 'workspace' : 'workspaces'}
          </p>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative flex-1 md:flex-initial md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search workspaces..."
              className="pl-9 h-10 rounded-xl bg-secondary/60 border-border"
              data-testid="dc-workspaces-search"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-10 rounded-xl tracking-[0.16em] uppercase text-[11px] font-semibold gap-1.5"
                data-testid="dc-workspaces-filter"
              >
                <SlidersHorizontal className="size-4" />
                {filter === 'all' ? 'Filter' : activeFilterLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1.5">
              {WORKSPACE_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onFilterChange(opt.value)}
                  className={cn(
                    'w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-accent hover:text-accent-foreground transition-colors',
                    filter === opt.value && 'bg-accent text-accent-foreground font-medium'
                  )}
                  data-testid={`dc-workspaces-filter-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          {onCreate && (
            <Button
              type="button"
              size="sm"
              onClick={onCreate}
              className="h-10 rounded-xl gap-1.5"
              data-testid="dc-create-workspace-card"
            >
              <Plus className="size-4" />
              <span className="hidden md:inline">New Workspace</span>
              <span className="md:hidden">New</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ----------------------- Film filter options ----------------------- */

const FILM_STATUS_FILTERS = [
  { value: 'all',             label: 'All statuses' },
  { value: 'IN_REVIEW',       label: 'In Review' },
  { value: 'PRE_PRODUCTION',  label: 'Pre-Production' },
  { value: 'IN_PRODUCTION',   label: 'In Production' },
  { value: 'ON_HOLD',         label: 'On Hold' },
  { value: 'COMPLETE',        label: 'Complete' },
];

const FILM_OWNERSHIP_FILTERS = [
  { value: 'all',    label: 'All films' },
  { value: 'owned',  label: 'Owned by you' },
  { value: 'member', label: 'Shared with you' },
];

const defaultFilmFilters = () => ({ status: 'all', ownership: 'all' });

const countActiveFilmFilters = (f) => {
  let n = 0;
  if (f.status !== 'all')    n += 1;
  if (f.ownership !== 'all') n += 1;
  return n;
};

const FilmRow = ({ film, currentUserId, workspaceLookup }) => {
  const status = deriveFilmStatus(film);
  const statusMeta = FILM_STATUS[status];
  const stage = deriveStageProgress(film, status);
  const editorTime = formatRelative(film.updated_at || film.created_at);
  const youAreOwner = currentUserId && (film.user_id === currentUserId || film.is_owner);
  const wsName = film.workspace_name || workspaceLookup?.[film.workspace_id]?.name;

  const linkPath = film.workspace_id
    ? `/workspaces/${film.workspace_id}/films/${film.id}`
    : `/films/${film.id}`;

  return (
    <Link
      to={linkPath}
      data-testid={`dc-film-row-${film.id}`}
      className="group flex items-center gap-4 p-4 md:p-5 hover:bg-muted/40 transition-colors"
    >
      {/* Thumbnail */}
      <FilmThumb film={film} />

      {/* Middle: title + meta + stage */}
      <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <h3 className="font-display font-semibold text-lg md:text-xl tracking-tight truncate">
              {film.title}
            </h3>
            <span
              className={cn(
                'shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase',
                statusMeta.className
              )}
              data-testid={`dc-film-status-${film.id}`}
            >
              {statusMeta.label}
            </span>
          </div>
          <p className="mt-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-muted-foreground truncate">
            {youAreOwner ? 'Edited by You' : 'Edited'}
            {editorTime ? ` · ${editorTime}` : ''}
            {wsName ? ` · ${wsName}` : ''}
          </p>
        </div>

        {/* Stage progress */}
        <div className="md:w-72 lg:w-80 md:shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
              {stage.label}
            </span>
            <span className="text-[11px] font-mono text-foreground">
              {stage.percent}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', progressBarColorClass(stage.percent, status))}
              style={{ width: `${Math.max(stage.percent, stage.percent === 0 ? 4 : stage.percent)}%`, opacity: stage.percent === 0 ? 0.4 : 1 }}
            />
          </div>
        </div>
      </div>

      <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
    </Link>
  );
};

const FilmThumb = ({ film }) => {
  const initial = titleInitial(film.title);
  if (film.poster_url) {
    return (
      <img
        src={film.poster_url}
        alt=""
        className="size-16 md:size-20 rounded-xl object-cover shrink-0 ring-1 ring-border"
      />
    );
  }
  return (
    <div
      aria-hidden
      className="size-16 md:size-20 rounded-xl shrink-0 flex items-center justify-center bg-gradient-to-br from-primary/30 to-warning/30 ring-1 ring-border"
    >
      <span className="font-display font-bold text-2xl text-foreground/80">{initial}</span>
    </div>
  );
};

const FilmRowSkeleton = () => (
  <div className="flex items-center gap-4 p-4 md:p-5">
    <Skeleton className="size-16 md:size-20 rounded-xl shrink-0" />
    <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="md:w-72 lg:w-80 md:shrink-0 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-1.5 w-full rounded-full" />
      </div>
    </div>
    <Skeleton className="size-4 rounded shrink-0" />
  </div>
);

const FilmsEmptyState = ({ onCreate, ctaLabel = 'Create Film' }) => (
  <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
    <div className="mx-auto size-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
      <FilmIcon className="size-7 text-primary" />
    </div>
    <h3 className="font-display font-semibold text-xl mb-1">No films yet</h3>
    <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-5">
      Create a film to start organising scenes, shots and crew under your workspaces.
    </p>
    {onCreate && (
      <Button onClick={onCreate} data-testid="dc-create-film-empty-cta" size="sm" className="rounded-xl">
        <Plus className="size-4 mr-1.5" /> {ctaLabel}
      </Button>
    )}
  </div>
);

const FilmsHeader = ({ search, onSearchChange, filters, onFiltersChange, count, totalCount, onClearFilters }) => {
  const activeCount = countActiveFilmFilters(filters);
  const statusLabel = FILM_STATUS_FILTERS.find((o) => o.value === filters.status)?.label || 'All';
  const ownershipLabel = FILM_OWNERSHIP_FILTERS.find((o) => o.value === filters.ownership)?.label || 'All';
  return (
    <div className="mb-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            Your Films
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {activeCount > 0
              ? `${count} of ${totalCount} ${totalCount === 1 ? 'film' : 'films'}`
              : `${count} ${count === 1 ? 'film' : 'films'}`}
          </p>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative flex-1 md:flex-initial md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search projects..."
              className="pl-9 h-10 rounded-xl bg-secondary/60 border-border"
              data-testid="dc-films-search"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-10 rounded-xl tracking-[0.16em] uppercase text-[11px] font-semibold gap-1.5"
                data-testid="dc-films-filter"
              >
                <SlidersHorizontal className="size-4" />
                Filter
                {activeCount > 0 && (
                  <span
                    className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold"
                    data-testid="dc-films-filter-count"
                  >
                    {activeCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold tracking-tight">Filter films</p>
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="text-[11px] font-semibold tracking-wide uppercase text-primary hover:text-primary/80"
                    data-testid="dc-films-filter-clear"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="px-3 py-2">
                <p className="px-1 py-1 text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                  Status — {statusLabel}
                </p>
                {FILM_STATUS_FILTERS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onFiltersChange({ ...filters, status: opt.value })}
                    className={cn(
                      'w-full text-left text-sm rounded-lg px-3 py-1.5 hover:bg-accent hover:text-accent-foreground transition-colors',
                      filters.status === opt.value && 'bg-accent text-accent-foreground font-medium'
                    )}
                    data-testid={`dc-films-filter-status-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-border">
                <p className="px-1 py-1 text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                  Ownership — {ownershipLabel}
                </p>
                {FILM_OWNERSHIP_FILTERS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onFiltersChange({ ...filters, ownership: opt.value })}
                    className={cn(
                      'w-full text-left text-sm rounded-lg px-3 py-1.5 hover:bg-accent hover:text-accent-foreground transition-colors',
                      filters.ownership === opt.value && 'bg-accent text-accent-foreground font-medium'
                    )}
                    data-testid={`dc-films-filter-ownership-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- main view ----------------------------- */

const DirectorsCommandView = ({
  workspaces = [],
  films = [],
  currentUserId,
  loading = false,
  showCreateFilmCta = false,
  onCreateFilm,
  onCreateWorkspace,
  onDeleteWorkspace,
  beforeHero,
  filmsLoading = false,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filmFilters, setFilmFilters] = useState(defaultFilmFilters());
  const [wsSearch, setWsSearch] = useState('');
  const [wsFilter, setWsFilter] = useState('all');

  const workspaceLookup = useMemo(() => {
    const m = {};
    for (const ws of workspaces) m[ws.id] = ws;
    return m;
  }, [workspaces]);

  const filteredFilms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return films.filter((f) => {
      // Status facet
      if (filmFilters.status !== 'all') {
        if (deriveFilmStatus(f) !== filmFilters.status) return false;
      }
      // Ownership facet — `is_owner` is the authoritative cross-workspace
      // flag when present, else fall back to user_id match.
      if (filmFilters.ownership !== 'all') {
        const owned = currentUserId && (f.user_id === currentUserId || f.is_owner);
        if (filmFilters.ownership === 'owned'  && !owned) return false;
        if (filmFilters.ownership === 'member' && owned) return false;
      }
      // Text search
      if (q) {
        const t = (f.title || '').toLowerCase();
        const d = (f.description || '').toLowerCase();
        if (!t.includes(q) && !d.includes(q)) return false;
      }
      return true;
    });
  }, [films, search, filmFilters, currentUserId]);

  const filmFiltersActive = countActiveFilmFilters(filmFilters) > 0 || !!search;

  const filteredWorkspaces = useMemo(() => {
    const q = wsSearch.trim().toLowerCase();
    return workspaces.filter((ws) => {
      const isOwner = ws.role === 'owner' || ws.owner_id === currentUserId;
      if (wsFilter === 'owned' && !isOwner) return false;
      if (wsFilter === 'member' && isOwner) return false;
      if (!q) return true;
      const n = (ws.name || '').toLowerCase();
      const d = (ws.description || '').toLowerCase();
      return n.includes(q) || d.includes(q);
    });
  }, [workspaces, wsSearch, wsFilter, currentUserId]);

  const wsFiltersActive = !!wsSearch || wsFilter !== 'all';

  const handleOpenWorkspace = (ws) => navigate(`/workspaces/${ws.id}`);
  const handleSettings     = (ws) => navigate(`/workspaces/${ws.id}/settings`);
  const handleMembers      = (ws) => navigate(`/workspaces/${ws.id}/members`);
  const handleDelete       = (ws) => {
    if (onDeleteWorkspace) onDeleteWorkspace(ws);
  };
  const handleCreateWs = () => {
    if (onCreateWorkspace) onCreateWorkspace();
    else navigate('/workspace/purchase');
  };

  if (loading && workspaces.length === 0) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-10 md:py-14">
        <Skeleton className="h-4 w-40 mb-3" />
        <Skeleton className="h-12 md:h-16 w-2/3 mb-10" />
        <div className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border mb-16">
          <WorkspaceRowSkeleton />
          <WorkspaceRowSkeleton />
          <WorkspaceRowSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="directors-command-view">
      {beforeHero}

      {/* Workspaces section */}
      <section className="mb-16" data-testid="dc-workspaces-section">
        <WorkspacesHeader
          search={wsSearch}
          onSearchChange={setWsSearch}
          filter={wsFilter}
          onFilterChange={setWsFilter}
          count={filteredWorkspaces.length}
          onCreate={handleCreateWs}
        />

        {filteredWorkspaces.length === 0 ? (
          <WorkspacesEmptyState
            onCreate={
              wsFiltersActive
                ? () => { setWsSearch(''); setWsFilter('all'); }
                : handleCreateWs
            }
            cleared={wsFiltersActive}
          />
        ) : (
          <div
            className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border"
            data-testid="dc-workspaces-list"
          >
            {filteredWorkspaces.map((ws) => (
              <WorkspaceRow
                key={ws.id}
                workspace={ws}
                isOwner={ws.role === 'owner' || ws.owner_id === currentUserId}
                onOpen={handleOpenWorkspace}
                onSettings={handleSettings}
                onMembers={handleMembers}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </section>

      {/* Films section */}
      <section data-testid="dc-films-section">
        <FilmsHeader
          search={search}
          onSearchChange={setSearch}
          filters={filmFilters}
          onFiltersChange={setFilmFilters}
          count={filteredFilms.length}
          totalCount={films.length}
          onClearFilters={() => setFilmFilters(defaultFilmFilters())}
        />

        {filmsLoading && films.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border">
            <FilmRowSkeleton />
            <FilmRowSkeleton />
            <FilmRowSkeleton />
          </div>
        ) : filteredFilms.length === 0 ? (
          <FilmsEmptyState
            // When search or filters are active and yield zero matches, the
            // CTA clears them instead of opening the create dialog.
            onCreate={
              filmFiltersActive
                ? () => { setSearch(''); setFilmFilters(defaultFilmFilters()); }
                : (showCreateFilmCta ? onCreateFilm : undefined)
            }
            ctaLabel={filmFiltersActive ? 'Clear filters' : 'Create Film'}
          />
        ) : (
          <div
            className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border"
            data-testid="dc-films-list"
          >
            {filteredFilms.map((film) => (
              <FilmRow
                key={film.id}
                film={film}
                currentUserId={currentUserId}
                workspaceLookup={workspaceLookup}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DirectorsCommandView;
